/**
 * Analytics Dashboard - No-code interface for tracking events and usage
 * FINRA-compliant: All data stored in your Supabase instance
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart3,
  Users,
  MousePointer,
  Eye,
  Clock,
  TrendingUp,
  Activity,
  Play,
  Calendar,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Color palette for charts
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

interface AnalyticsSummary {
  total_events: number;
  unique_users: number;
  total_sessions: number;
  avg_session_duration_ms: number;
  top_pages: Array<{ page_path: string; views: number }>;
  top_events: Array<{ event_name: string; count: number }>;
}

interface AnalyticsEvent {
  id: string;
  user_id: string | null;
  anonymous_id: string;
  event_name: string;
  event_category: string | null;
  page_path: string;
  device_type: string;
  created_at: string;
  properties: Record<string, unknown>;
}

interface SessionRecording {
  id: string;
  session_id: string;
  user_id: string | null;
  started_at: string;
  ended_at: string | null;
  duration_ms: number;
  page_url: string;
  event_count: number;
}

export default function Analytics() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('7');
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch analytics summary
  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['analytics-summary', dateRange],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_analytics_summary', {
        days_back: parseInt(dateRange),
      });
      if (error) throw error;
      return data?.[0] as AnalyticsSummary;
    },
  });

  // Fetch recent events
  const { data: recentEvents = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['analytics-events', dateRange],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('analytics_events')
        .select('*')
        .gte('created_at', new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as AnalyticsEvent[];
    },
  });

  // Fetch events over time (for chart)
  const { data: eventsOverTime = [] } = useQuery({
    queryKey: ['analytics-events-timeline', dateRange],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('analytics_events')
        .select('created_at, event_name')
        .gte('created_at', new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: true });
      if (error) throw error;

      // Group by day
      const grouped: Record<string, { date: string; events: number; page_views: number; clicks: number }> = {};
      (data as AnalyticsEvent[]).forEach(event => {
        const date = new Date(event.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!grouped[date]) {
          grouped[date] = { date, events: 0, page_views: 0, clicks: 0 };
        }
        grouped[date].events++;
        if (event.event_name === 'page_view') grouped[date].page_views++;
        if (event.event_name === 'element_click') grouped[date].clicks++;
      });

      return Object.values(grouped);
    },
  });

  // Fetch session recordings
  const { data: recordings = [], isLoading: recordingsLoading } = useQuery({
    queryKey: ['session-recordings', dateRange],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('session_recordings')
        .select('id, session_id, user_id, started_at, ended_at, duration_ms, page_url, event_count')
        .gte('started_at', new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString())
        .order('started_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as SessionRecording[];
    },
  });

  // Format duration
  const formatDuration = (ms: number) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Format relative time
  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Get event category badge color
  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'navigation': return 'bg-blue-500/10 text-blue-500';
      case 'interaction': return 'bg-green-500/10 text-green-500';
      case 'engagement': return 'bg-purple-500/10 text-purple-500';
      case 'conversion': return 'bg-amber-500/10 text-amber-500';
      case 'error': return 'bg-red-500/10 text-red-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Analytics Dashboard
                </h1>
                <p className="text-sm text-muted-foreground">Track events and user behavior</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[140px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => refetchSummary()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Total Events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-2xl font-bold">{summary?.total_events?.toLocaleString() || 0}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Unique Users
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-2xl font-bold">{summary?.unique_users?.toLocaleString() || 0}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-2xl font-bold">{summary?.total_sessions?.toLocaleString() || 0}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Avg. Session
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-2xl font-bold">{formatDuration(summary?.avg_session_duration_ms || 0)}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-2">
              <MousePointer className="h-4 w-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="recordings" className="gap-2">
              <Play className="h-4 w-4" />
              Recordings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Events Over Time Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Events Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={eventsOverTime}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" className="text-xs" />
                        <YAxis className="text-xs" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Line type="monotone" dataKey="events" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="page_views" stroke="#10b981" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="clicks" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-muted-foreground">All Events</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-muted-foreground">Page Views</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-muted-foreground">Clicks</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summary?.top_events || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis type="number" className="text-xs" />
                        <YAxis dataKey="event_name" type="category" width={120} className="text-xs" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={summary?.top_pages || []}
                          dataKey="views"
                          nameKey="page_path"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ page_path }) => page_path || '/'}
                        >
                          {(summary?.top_pages || []).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Device Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {recentEvents.slice(0, 10).map((event) => (
                        <div key={event.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className={getCategoryColor(event.event_category)}>
                              {event.event_name}
                            </Badge>
                            <span className="text-sm text-muted-foreground truncate max-w-[150px]">
                              {event.page_path}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(event.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Event Log</CardTitle>
                <CardDescription>All tracked events in the selected time range</CardDescription>
              </CardHeader>
              <CardContent>
                {eventsLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Page</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell className="font-medium">{event.event_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getCategoryColor(event.event_category)}>
                              {event.event_category || 'other'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {event.page_path}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{event.device_type}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs font-mono">
                            {event.user_id?.slice(0, 8) || event.anonymous_id?.slice(0, 8) || 'anon'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatRelativeTime(event.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recordings">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Session Recordings</CardTitle>
                <CardDescription>Watch user sessions to understand behavior (inputs masked for compliance)</CardDescription>
              </CardHeader>
              <CardContent>
                {recordingsLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : recordings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recordings yet</p>
                    <p className="text-sm">Sessions will appear here as users interact with the app</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recordings.map((recording) => (
                      <div
                        key={recording.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/analytics/recording/${recording.session_id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Play className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              Session {recording.session_id.slice(0, 8)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {recording.page_url}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-right">
                            <p className="text-muted-foreground">Duration</p>
                            <p className="font-medium">{formatDuration(recording.duration_ms)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-muted-foreground">Events</p>
                            <p className="font-medium">{recording.event_count}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-muted-foreground">Time</p>
                            <p className="font-medium">{formatRelativeTime(recording.started_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
